import { capitalize, getRequiredFields } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { schema } from "./validations";
import { toast } from "@/components/Toast";
import {
  useGetBillingCategoryDropdown,
  useGetDocumentTypeDropdown,
  useGetGlMasterDataDropdown,
  useGetParkDocById,
  useGetPaymentTerms,
  useGetPropertyBlocks,
  useGetPropertyModels,
} from "@/services";
import {
  BILLING_AMOUNT,
  BILLING_CATEGORY,
  BILLING_DATE,
  BILLING_MONTH_YEAR,
  CHECK,
  COMMENT,
  CURR_OWNER_ID,
  CURR_OWNER_LAST_NAME,
  DOCUMENT_ID,
  DOCUMENT_TYPE,
  DRCR,
  DUE_DATE,
  GL_ACCOUNT,
  LINE_BILLING_AMOUNT,
  LINE_ITEMS,
  PAYMENT_TERM,
  POSTING_DATE,
  PROPERTY_BLOCK,
  PROPERTY_ID,
  PROPERTY_NAME,
  PROPERTY_MODEL,
  STREET_ADDRESS,
  TRANSACTION_TYPE,
  UNIT_NO,
} from "./consts";
import moment from "moment";

const useAddEdit = () => {
  const { billingId, action } = useParams();
  const navigate = useNavigate();
  const [keys, setKeys] = useState({
    accountName: Math.random() * 2,
    documentTypes: Math.random() * 2,
    paymentTerm: Math.random() * 2,
    billingCategory: Math.random() * 2,
    propertyModel: Math.random() * 2,
    transactionType: Math.random() * 2,
  });

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const form = useForm<any>({
    defaultValues: {
      [DOCUMENT_TYPE]: "",
      [DOCUMENT_ID]: "",
      [BILLING_MONTH_YEAR]: "",
      [BILLING_DATE]: "",
      [BILLING_AMOUNT]: "",
      [DUE_DATE]: "",
      [TRANSACTION_TYPE]: "",
      [GL_ACCOUNT]: "",
      [PAYMENT_TERM]: "",
      [COMMENT]: "",
      [POSTING_DATE]: "",
      // [POSTING_MONTH]: "",
      // [YEAR]: "",
      [LINE_ITEMS]: [
        {
          [BILLING_CATEGORY]: "",
          [PROPERTY_MODEL]: "",
          [PROPERTY_BLOCK]: "",
          [PROPERTY_ID]: "",
          [PROPERTY_NAME]: "",
          [UNIT_NO]: "",
          [STREET_ADDRESS]: "",
          [CURR_OWNER_ID]: "",
          [CURR_OWNER_LAST_NAME]: "",
          [DRCR]: "",
          [LINE_BILLING_AMOUNT]: "",
          [CHECK]: false,
        },
      ],
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);
  
  const breadcrumbData = useMemo(() => {
    return [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.cam}/${routes.activityDashboard}/${routes.billingLog}`,
        name: <span className="text-primary">{"Billing Log"}</span>,
      },
      {
        link: "",
        name: <span className="text-primary">{capitalize(action || "")}</span>,
      },
    ];
  }, []);

  const breadcrumbTitle = `Billing Log - ${communityDetail?.name}`;

  const onBackClick = () => {
    navigate(`/${routes.cam}/${routes.activityDashboard}/${routes.billingLog}`);
  };

  const { data: documentTypes, isLoading: isDocumentTypeLoading } =
    useGetDocumentTypeDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterDataDropdown({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: paymentTermOptions, isLoading: isPaymentTermLoading } =
    useGetPaymentTerms();

  const { data: propertyModels, isLoading: isPropertyModelLoading } =
    useGetPropertyModels();

  const { data: propertyBlocks, isLoading: isPropertyBlockLoading } =
    useGetPropertyBlocks();

  const { data: billingCategory, isLoading: isBillingCategoryLoading } =
  useGetBillingCategoryDropdown({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    if (documentTypes) {
      if (!billingId) {
        const documentTypeId = documentTypes
          ?.find((data: any) => {
            return data?.documentType?.toLowerCase() == "ob";
          })
          ?.documentTypeId?.toString();
        if (documentTypeId) {
          form.setValue(DOCUMENT_TYPE, documentTypeId);
        } else {
          toast.error(
            "The Document Type 'OB' was not found. Kindly add it to proceed."
          );
          onBackClick();
        }
      }
      setKeys((prev: any) => ({ ...prev, documentTypes: Math.random() * 4 }));
    }
  }, [documentTypes]);

  useEffect(() => {
    if (paymentTermOptions) {
      setKeys((prev: any) => ({ ...prev, paymentTerm: Math.random() * 4 }));
    }
  }, [paymentTermOptions]);

  const { data: pendingBillingData, isLoading: isLoadingBillingData } =
    useGetParkDocById({
      id: billingId as string,
      queryParams: {
        enabled: !!billingId,
      },
    });

  useEffect(() => {
    if (pendingBillingData) {
      const getBillingCategoryName = (item: any) => {
        const category = billingCategory?.data?.find(
          (data: any) =>
            data?.propertyModelId?.toString() == item?.property?.modelId?.toString() ||
            data?.id?.toString() ==
            (item?.billingCategoryId ?? item?.[BILLING_CATEGORY])?.toString()
        );

        return category?.name ?? "";
      };

      form.reset({
        [DOCUMENT_TYPE]:
          pendingBillingData?.data?.header?.[DOCUMENT_TYPE]?.toString() || "",
        [DOCUMENT_ID]:
          pendingBillingData?.data?.header?.documentNumber?.toString() ||
          pendingBillingData?.data?.header?.parkDocumentHeaderId?.toString() ||
          "",
        [BILLING_MONTH_YEAR]:
          `${pendingBillingData?.data?.header?.postingPeriod?.toString()}/${pendingBillingData?.data?.header?.postingYear?.toString()}` ||
          "",
        [BILLING_DATE]:
          pendingBillingData?.data?.header?.pricingResponse?.[
            TRANSACTION_TYPE
          ]?.toUpperCase() == "MONTHLY_HOA_FEE" &&
          pendingBillingData?.data?.header?.billingLogResponse?.createdAt
            ? pendingBillingData?.data?.header?.billingLogResponse?.createdAt?.toString()
            : pendingBillingData?.data?.header?.pricingResponse?.[BILLING_DATE]
            ? pendingBillingData?.data?.header?.pricingResponse?.[
                BILLING_DATE
              ]?.toString()
            : "",
        [BILLING_AMOUNT]:
          pendingBillingData?.data?.header?.approvedAmount?.toString() || "",
        [DUE_DATE]: pendingBillingData?.data?.header?.[DUE_DATE]
          ? moment(pendingBillingData?.data?.header?.[DUE_DATE]).toString()
          : "",
        [TRANSACTION_TYPE]:
          pendingBillingData?.data?.header?.pricingResponse?.[
            TRANSACTION_TYPE
          ]?.toString() || "",
        [GL_ACCOUNT]:
          pendingBillingData?.data?.header?.pricingResponse?.[
            GL_ACCOUNT
          ]?.toString() || "",
        [PAYMENT_TERM]:
          pendingBillingData?.data?.header?.pricingResponse?.[
            PAYMENT_TERM
          ]?.toString() || "",
        [COMMENT]: pendingBillingData?.data?.header?.comments?.toString() || "",
        [POSTING_DATE]: pendingBillingData?.data?.header?.[POSTING_DATE]
          ? moment(pendingBillingData?.data?.header?.[POSTING_DATE])?.toString()
          : "",
        [LINE_ITEMS]: pendingBillingData?.data?.lineItems?.map((item: any) => ({
          ...item,
          [BILLING_CATEGORY]: getBillingCategoryName(item),
          [PROPERTY_MODEL]: item?.property?.[PROPERTY_MODEL]?.toString() || "",
          [PROPERTY_BLOCK]: item?.property?.[PROPERTY_BLOCK]?.toString() || "",
          [PROPERTY_ID]: item?.property?.[PROPERTY_ID]?.toString() || "",
          [PROPERTY_NAME]: item?.property?.name || "",
          [UNIT_NO]: item?.property?.[UNIT_NO]?.toString() || "",
          [STREET_ADDRESS]:
            item?.property?.address?.[STREET_ADDRESS]?.toString() || "",
          [CURR_OWNER_ID]:
            item?.propertyOwnership?.[CURR_OWNER_ID]?.toString() || "",
          [CURR_OWNER_LAST_NAME]:
            item?.propertyOwnership?.[CURR_OWNER_LAST_NAME]?.toString() || "",
          [DRCR]: item?.drCr?.toString() || "",
          [LINE_BILLING_AMOUNT]: item?.[LINE_BILLING_AMOUNT]?.toString() || "",
          [CHECK]: item?.check || false,
        })),
      });
      setKeys({
        accountName: Math.random() * 2,
        documentTypes: Math.random() * 2,
        paymentTerm: Math.random() * 2,
        billingCategory: Math.random() * 2,
        propertyModel: Math.random() * 2,
        transactionType: Math.random() * 2,
      });
    }
  }, [pendingBillingData, billingCategory]);

  return {
    breadcrumbData,
    onBackClick,
    communityDetail,
    form,
    requiredFields,
    glData,
    isDocumentTypeLoading,
    documentTypes,
    isLoadingGlMaster,
    onCancel: onBackClick,
    breadcrumbTitle,
    keys,
    isPaymentTermLoading,
    paymentTermOptions,
    propertyModels,
    isPropertyModelLoading,
    propertyBlocks,
    isPropertyBlockLoading,
    billingCategory,
    isBillingCategoryLoading,
    isLoadingBillingData,
  };
};

export default useAddEdit;
